import { Button } from '@/components/ui/Button';
import { ItemsPerRowSelector } from '@/components/ui/custom-ui/ItemsPerRowSelector';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { ErrorBoundaryFallbackRender } from '@/components/ui/ErrorBoundaryFallback';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import appConfig from '@/config';
import {
  Device,
  DeviceBoolExp,
  DeviceOrderBy,
  OrderBy,
} from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import { MediaKind } from '@/models/device.models';
import { SearchParam } from '@/models/search-param.models';
import { getGridColsClass } from '@/utils/ui.function';
import { useQuery } from '@tanstack/react-query';
import { Eye, EyeOff, LayoutGrid, Plus, Square } from 'lucide-react';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useSearchParams } from 'react-router';
import { useDebounce } from 'use-debounce';
import { CompareDevicesPanel } from '../components/CompareDevicesPanel';
import {
  devicesQuery,
  devicesQueryNoApiKeyDescription,
} from '../graphql/query';

type CompareDevicePageConfig = {
  panels: {
    deviceId: string;
    timestamp?: string;
    kind: MediaKind;
  }[];
  showRois?: boolean;
  layout?: 'grid' | 'tabs';
};

type PanelProps = {
  device: Device;
  mediaKind: MediaKind;
  timestamp?: string;
};

/**
 * STATE FLOW:
 * -----------
 * 1. URL -> Config: Parse ?config query param into state on mount
 * 2. Config -> Device IDs: Extract device IDs from config
 * 3. Device IDs -> Query: Fetch device data from GraphQL API
 * 4. Device Data + Config -> Panels: Merge device data with config to create panel props
 *
 * Example URL:
 * ?config={"panels":[{"deviceId":"123","timestamp":"2025-10-11T00:44:09+00:00","kind":"Processed"}]}
 */
export function CompareDevicePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [devices, setDevices] = useState<Device[]>([]);
  const [searchDevices, setSearchDevices] = useState<Device[]>([]);
  const [isAddDeviceDialogOpen, setIsAddDeviceDialogOpen] =
    useState<boolean>(false);
  const [panelProps, setPanelProps] = useState<PanelProps[]>([]);
  const [config, setConfig] = useState<CompareDevicePageConfig>();
  const [nameFilter, setNameFilter] = useState<string>();
  const [debouncedNameFilter] = useDebounce(nameFilter, 300);
  const [numberOfColumns, setNumberOfColumns] = useState(2);
  const [viewMode, setViewMode] = useState<'grid' | 'tabs'>('grid');
  const [activePanelIndex, setActivePanelIndex] = useState<number>(0);
  // Fetches devices specified in URL config
  const fetchDevicesFromQueryParams = useRef(true);
  const showFilteredDevices = useRef(false);

  const [filters, setFilters] = useState<{
    where: DeviceBoolExp;
    orderBy: DeviceOrderBy[];
  }>({
    orderBy: [
      {
        name: OrderBy.Asc,
      },
    ],
    where: {},
  });

  const {
    data: devicesData,
    isFetching,
    isLoading,
  } = useQuery({
    queryKey: ['Devices', filters],
    queryFn: () =>
      execute({
        query: appConfig.apiKey.hasDescription
          ? devicesQuery
          : devicesQueryNoApiKeyDescription,
        variables: {
          where: filters.where,
        },
      }),
    select: (data) => data,
    enabled: Object.keys(filters.where).length > 0,
    staleTime: 3000,
  });

  useEffect(() => {
    if (!devicesData) {
      return;
    }
    if (showFilteredDevices.current) {
      setSearchDevices(devicesData.device as Device[]);
      showFilteredDevices.current = false;
      return;
    }
    setDevices(devicesData.device as Device[]);
  }, [devicesData]);

  useEffect(() => {
    if (!devices) {
      return;
    }
    if (config) {
      // Merge device data with config to create panel props
      setPanelProps(
        config.panels.map(
          (panel: {
            deviceId: string;
            kind: MediaKind;
            timestamp?: string;
          }) => {
            const device = devices.find((d) => d.id === panel.deviceId);
            return {
              timestamp: panel.timestamp,
              device: device!,
              mediaKind: panel.kind,
            };
          },
        ) || [],
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices]);

  useEffect(() => {
    if (config?.layout === 'tabs') {
      setViewMode('tabs');
      return;
    }
    setViewMode('grid');
  }, [config?.layout]);

  useEffect(() => {
    if (!panelProps.length) {
      setActivePanelIndex(0);
      return;
    }
    setActivePanelIndex((prev) =>
      prev >= panelProps.length ? panelProps.length - 1 : prev,
    );
  }, [panelProps.length]);

  useEffect(() => {
    if (debouncedNameFilter === undefined) {
      return;
    }
    if (debouncedNameFilter.trim() === '') {
      setFilters((prev) => ({
        ...prev,
        where: {},
      }));
      setSearchDevices([]);
      return;
    }

    setFilters((prev) => ({
      ...prev,
      where: {
        name: {
          _ilike: `%${debouncedNameFilter.trim()}%`,
        },
      },
    }));
    showFilteredDevices.current = true;
  }, [debouncedNameFilter]);

  useEffect(() => {
    if (searchParams.size > 0) {
      try {
        const searchParamsConfig = JSON.parse(
          searchParams.get(SearchParam.CONFIG) ?? '',
        ) as CompareDevicePageConfig;

        if (searchParamsConfig) {
          setConfig(searchParamsConfig);
        }
      } catch {
        console.log('Error parsing config from query params');
      }
    } else {
      setIsAddDeviceDialogOpen(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (config && fetchDevicesFromQueryParams.current) {
      // Set Where Filter to issue a fetch devices from URL config
      const ids = config.panels.map((p) => p.deviceId);
      setFilters((prev) => ({
        ...prev,
        where: {
          id: { _in: ids },
        },
      }));
      fetchDevicesFromQueryParams.current = false;
    }
  }, [config]);

  const handleShowRoisChange = (showRois: boolean) => {
    if (config) {
      config.showRois = showRois;
      setSearchParams({
        config: JSON.stringify(config),
      });
    }
  };

  const handleTimestampChange = (index: number, timestamp?: string) => {
    if (config?.panels) {
      config.panels[index].timestamp = timestamp;
      setSearchParams({
        config: JSON.stringify(config),
      });
    }
  };

  const handleSetMediaKind = (mediaKind: MediaKind, index: number) => {
    if (config?.panels) {
      config.panels[index].kind = mediaKind;
      setSearchParams({
        config: JSON.stringify({
          ...config,
        }),
      });
    }
  };

  const handleToggleViewMode = () => {
    const nextMode = viewMode === 'grid' ? 'tabs' : 'grid';
    setViewMode(nextMode);

    const updatedConfig: CompareDevicePageConfig = {
      ...(config ?? { panels: [] }),
      layout: nextMode,
    };

    setSearchParams({
      config: JSON.stringify(updatedConfig),
    });
  };

  const handleNameFilterValueChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setNameFilter(event.target.value ?? '');
  };

  const handleAddNewDevice = (device: Device) => {
    const updatedConfig: CompareDevicePageConfig = {
      ...(config ?? { panels: [] }),
      panels: [
        ...(config?.panels ?? []),
        {
          deviceId: device.id,
          kind: MediaKind.PROCESSED,
        },
      ],
    };

    setSearchParams({
      config: JSON.stringify(updatedConfig),
    });
    fetchDevicesFromQueryParams.current = true;
  };

  const handleRemoveClick = (index: number) => {
    const newConfig = { ...config } as CompareDevicePageConfig;
    if (newConfig?.panels) {
      newConfig.panels.splice(index, 1);
      setSearchParams(
        newConfig?.panels?.length
          ? {
              config: JSON.stringify(newConfig),
            }
          : {},
      );
    }
    setPanelProps((prev) => {
      const newPanelProps = [...prev];
      newPanelProps.splice(index, 1);
      return newPanelProps;
    });
  };

  return (
    <ErrorBoundary fallbackRender={ErrorBoundaryFallbackRender}>
      <>
        {viewMode === 'tabs' ? (
          <Tabs
            value={activePanelIndex.toString()}
            onValueChange={(value) =>
              setActivePanelIndex(Number.parseInt(value, 10) || 0)
            }
            className="flex flex-col gap-4"
          >
            <div className="flex flex-wrap items-center justify-between gap-3 align-middle">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-muted-foreground text-md font-normal tracking-tight">
                  Compare Devices
                </span>
                {panelProps.length > 0 && (
                  <TabsList className="flex-wrap">
                    {panelProps.map(({ device }, index) => {
                      const tabValue = index.toString();
                      const label =
                        device?.name ?? device?.id ?? `Panel ${index + 1}`;
                      return (
                        <TabsTrigger key={tabValue} value={tabValue}>
                          {label}
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant={'outline'}
                  size={'sm'}
                  onClick={() => handleShowRoisChange(!config?.showRois)}
                >
                  {config?.showRois ? <EyeOff /> : <Eye />} ROIs
                </Button>

                <Button
                  variant={'outline'}
                  size={'sm'}
                  onClick={() => setIsAddDeviceDialogOpen(true)}
                >
                  <Plus className="mr-2 h-4 w-4" /> Add Devices
                </Button>

                <Button
                  variant={'outline'}
                  size={'sm'}
                  onClick={handleToggleViewMode}
                >
                  <LayoutGrid />
                </Button>
              </div>
            </div>
            {panelProps.length > 0 ? (
              panelProps.map(({ device, mediaKind, timestamp }, index) => {
                const tabValue = index.toString();
                const key = `${device.id}-${mediaKind}-${timestamp ?? 'no-ts'}-${index}`;
                return (
                  <TabsContent
                    key={key}
                    value={tabValue}
                    className="mt-2"
                    forceMount
                  >
                    <CompareDevicesPanel
                      timestamp={timestamp}
                      device={device}
                      initialMediaKind={mediaKind}
                      onTimestampChange={(newTimestamp) =>
                        handleTimestampChange(index, newTimestamp)
                      }
                      onMediaKindChange={(newMediaKind: MediaKind) =>
                        handleSetMediaKind(newMediaKind, index)
                      }
                      onRemoveClick={() => handleRemoveClick(index)}
                      displayPolygons={config?.showRois}
                    />
                  </TabsContent>
                );
              })
            ) : (
              <div className="border-muted text-muted-foreground flex h-48 items-center justify-center rounded-md border border-dashed">
                No devices selected
              </div>
            )}
          </Tabs>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 align-middle">
              <div className="flex items-center space-x-2">
                <span className="text-muted-foreground text-md font-normal tracking-tight">
                  Compare Devices
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <ItemsPerRowSelector
                  value={numberOfColumns}
                  onChange={setNumberOfColumns}
                  max={4}
                />

                <Button
                  variant={'outline'}
                  size={'sm'}
                  onClick={() => handleShowRoisChange(!config?.showRois)}
                >
                  {config?.showRois ? <EyeOff /> : <Eye />} ROIs
                </Button>

                <Button
                  variant={'outline'}
                  size={'sm'}
                  onClick={() => setIsAddDeviceDialogOpen(true)}
                >
                  <Plus className="mr-2 h-4 w-4" /> Add Devices
                </Button>

                <Button
                  variant={'outline'}
                  size={'sm'}
                  onClick={handleToggleViewMode}
                >
                  <Square />
                </Button>
              </div>
            </div>
            <div className={`${getGridColsClass(numberOfColumns)} grid gap-4`}>
              {panelProps.map(({ device, mediaKind, timestamp }, index) => (
                <CompareDevicesPanel
                  timestamp={timestamp}
                  key={device.id + mediaKind + timestamp + index}
                  device={device}
                  initialMediaKind={mediaKind}
                  onTimestampChange={(newTimestamp) =>
                    handleTimestampChange(index, newTimestamp)
                  }
                  onMediaKindChange={(newMediaKind: MediaKind) =>
                    handleSetMediaKind(newMediaKind, index)
                  }
                  onRemoveClick={() => handleRemoveClick(index)}
                  displayPolygons={config?.showRois}
                />
              ))}
            </div>
          </>
        )}
        <Dialog
          open={isAddDeviceDialogOpen}
          onOpenChange={setIsAddDeviceDialogOpen}
        >
          <DialogContent className="sm:max-w-3xl">
            <DialogHeader>
              <DialogTitle>Add Devices</DialogTitle>
              <DialogDescription />
            </DialogHeader>
            <div className="flex flex-col gap-4">
              <Input
                placeholder="Filter by name or id..."
                name="deviceName"
                onChange={handleNameFilterValueChange}
                value={nameFilter ?? ''}
              />
              {(isFetching || isLoading) && nameFilter ? (
                <div className="flex h-12 flex-grow items-center justify-center">
                  <Spinner className="h-5" />
                </div>
              ) : (
                <div className="@container">
                  <div className="grid gap-2 @min-[300px]:grid-cols-2 @min-[600px]:grid-cols-3 @min-[800px]:grid-cols-4">
                    {searchDevices.map((device) => (
                      <Button
                        key={device.id}
                        variant={'outline'}
                        className="w-full"
                        onClick={() => handleAddNewDevice(device)}
                      >
                        <span className="truncate">{device?.name}</span>
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </>
    </ErrorBoundary>
  );
}
